/**
 * TagInput Component
 *
 * Chips-style editor for a list of short string values. Type a value and press
 * Enter (or comma) to add it; click the X on a chip to remove it. Values are
 * de-duplicated case-insensitively.
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export interface TagInputProps {
  /** Field label */
  label?: string;
  /** Current list of tags */
  value: string[];
  /** Change handler */
  onChange: (next: string[]) => void;
  /** Placeholder shown when the list is empty */
  placeholder?: string;
  /** Disable adding/removing */
  disabled?: boolean;
  /** Maximum length of a single tag */
  maxLength?: number;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const TagInput: React.FC<TagInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  maxLength = 255,
}) => {
  const [draft, setDraft] = useState('');

  const addTag = (raw: string) => {
    const trimmed = raw.trim().slice(0, maxLength);
    if (!trimmed) return;
    const exists = value.some((v) => v.toLowerCase() === trimmed.toLowerCase());
    if (!exists) onChange([...value, trimmed]);
    setDraft('');
  };

  const removeTag = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(draft);
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      removeTag(value.length - 1);
    }
  };

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
        </label>
      )}
      <div className="flex flex-wrap items-center gap-1.5 min-h-[44px] px-3 py-1.5 rounded-lg border border-gray-300 bg-white focus-within:border-[#C00035] focus-within:ring-1 focus-within:ring-[#C00035] transition-colors">
        {value.map((tag, index) => (
          <span
            key={`${tag}-${index}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm bg-[#C00035]/10 border border-[#C00035]/20 text-[#C00035]"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeTag(index)}
                aria-label={`Remove ${tag}`}
                className="inline-flex items-center justify-center text-[#C00035] hover:text-[#900027]"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            value={draft}
            maxLength={maxLength}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => addTag(draft)}
            placeholder={value.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[120px] border-none outline-none bg-transparent text-sm text-gray-900 placeholder-gray-400 py-1"
          />
        )}
      </div>
    </div>
  );
};

export default TagInput;
